import { toUserFacingApiError } from "lib/utils/userFacingApiError";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import c13Image from "assets/imgs/payload-gimbal/c13.png";
import zr10Image from "assets/imgs/payload-gimbal/zr10.png";
import patrolImage from "assets/imgs/payload-general/patrol-module.png";
import { ReactComponent as ControlArmIcon } from "assets/svgs/navbar/robot-arm-svgrepo-com.svg";
import FunctionalButton from "components/layout/FunctionalButton";
import SkydroidThermalSettingsDialog from "components/control/settings/SkydroidThermalSettingsDialog";
import PatrolModuleSettingsDialog from "components/control/settings/PatrolModuleSettingsDialog";
import Insta360SettingsPanel from "components/control/settings/Insta360SettingsPanel";
import { useARC } from "providers/ARC/ARCProvider";
import { useLightControl } from "providers/ARC/LightControlProvider";
import { useFeatures } from "providers/FeaturesProvider";
import { useArmControl } from "providers/ArmControlProvider";
import { useToast } from "providers/ToastProvider";
import {
    deleteGimbalGatewayProfile,
    getGimbalGatewayProfile,
    getGimbalGatewayBaseUrl,
    postGimbalGatewayProfile,
    type GimbalGatewayProfileId,
} from "lib/gimbalGatewayApi";
import useIsHttpSecure from "lib/utils/useIsHttpSecure";
import useFleetArcId from "lib/utils/useFleetArcId";
import { ConnectionState } from "lib/consts/ARC/connection";

const PAYLOAD_OPTIONS: {
    id: GimbalGatewayProfileId;
    image: string;
}[] = [
    {
        id: "skydroid",
        image: c13Image,
    },
    {
        id: "zr10",
        image: zr10Image,
    },
];

const PayloadSettingsPanel: React.FC = () => {
    const { t } = useTranslation(["settings", "common"]);
    const { addToast } = useToast();
    const { selectedARC, connectionState } = useARC();
    const { isSecurityModuleConnected } = useLightControl();
    const { hasArm } = useFeatures();
    const { armState } = useArmControl();
    const isSecure = useIsHttpSecure();
    const arcId = useFleetArcId();
    const baseUrl = useMemo(
        () => getGimbalGatewayBaseUrl(selectedARC?.host ?? null, isSecure, arcId),
        [selectedARC?.host, isSecure, arcId]
    );

    const isArcConnected = connectionState === ConnectionState.Connected;
    const armConnected = isArcConnected && hasArm;
    const armStatusLabel = !isArcConnected
        ? t("payload.arm.arcDisconnected")
        : armConnected
          ? armState?.demo
              ? t("payload.arm.demo")
              : t("payload.arm.connected")
          : t("payload.arm.disconnected");
    const armHint = !isArcConnected
        ? t("payload.arm.connectArcHint")
        : armConnected
          ? armState?.busy
              ? t("payload.arm.busyHint")
              : t("payload.arm.connectedHint")
          : t("payload.arm.disconnectedHint");

    const [selected, setSelected] = useState<GimbalGatewayProfileId | null>(null);
    const [currentProfile, setCurrentProfile] = useState<GimbalGatewayProfileId | null>(null);
    const [busy, setBusy] = useState(false);
    const [loadingSelected, setLoadingSelected] = useState(false);
    const [thermalDialogOpen, setThermalDialogOpen] = useState(false);
    const [patrolDialogOpen, setPatrolDialogOpen] = useState(false);

    const toUserFacingError = useCallback((error: unknown, fallback: string) => {
        const raw = error instanceof Error ? error.message : String(error ?? "");
        const msg = raw.trim();
        if (!msg) return fallback;
        if (msg.includes("is not connected")) {
            return t("payload.gimbalNotConnected");
        }
        return toUserFacingApiError(msg, fallback);
    }, [t]);

    const refreshSelectedProfile = useCallback(async () => {
        if (!baseUrl) {
            setSelected(null);
            setCurrentProfile(null);
            setLoadingSelected(false);
            return;
        }
        setLoadingSelected(true);
        try {
            const data = await getGimbalGatewayProfile(baseUrl);
            const profile = data.profile;
            if (profile === "skydroid" || profile === "zr10") {
                setCurrentProfile(profile);
                setSelected((prev) => prev ?? profile);
            } else {
                setCurrentProfile(null);
            }
        } catch {
            setCurrentProfile(null);
        } finally {
            setLoadingSelected(false);
        }
    }, [baseUrl]);

    useEffect(() => {
        void refreshSelectedProfile();
    }, [refreshSelectedProfile]);

    const applyProfile = useCallback(async () => {
        if (!baseUrl || !selected || busy) return;
        setBusy(true);
        try {
            await postGimbalGatewayProfile(baseUrl, selected);
            await refreshSelectedProfile();
            const label =
                t(`payload.options.${selected}.title`);
            addToast({
                type: "success",
                message: t("payload.selectedOnRobot", { label }),
            });
        } catch (e) {
            addToast({
                type: "error",
                message: toUserFacingError(e, t("payload.updateFailed")),
            });
        } finally {
            setBusy(false);
        }
    }, [addToast, baseUrl, busy, refreshSelectedProfile, selected, toUserFacingError, t]);

    const disconnectProfile = useCallback(async () => {
        if (!baseUrl || busy) return;
        setBusy(true);
        try {
            await deleteGimbalGatewayProfile(baseUrl);
            await refreshSelectedProfile();
            addToast({
                type: "success",
                message: t("payload.disconnectedProfile"),
            });
        } catch (e) {
            addToast({
                type: "error",
                message: toUserFacingError(
                    e,
                    t("payload.disconnectFailed")
                ),
            });
        } finally {
            setBusy(false);
        }
    }, [addToast, baseUrl, busy, refreshSelectedProfile, toUserFacingError, t]);

    useEffect(() => {
        if (!selected) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key !== "Enter") return;
            const t = e.target as HTMLElement | null;
            if (
                t &&
                (t.tagName === "INPUT" ||
                    t.tagName === "TEXTAREA" ||
                    t.isContentEditable)
            ) {
                return;
            }
            e.preventDefault();
            void applyProfile();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [applyProfile, selected]);

    return (
        <div className="flex max-w-3xl flex-col gap-4">
            <div>
                <p className="text-h4 font-semibold text-white">{t("payload.title")}</p>
                <p className="mt-1 text-h5 text-black40">{t("payload.caption")}</p>
            </div>

            <div className="flex flex-col gap-3">
                <p className="text-h4 font-semibold text-white">{t("payload.armTitle")}</p>
                <p className="text-h5 text-black40">{t("payload.armCaption")}</p>
                <div className="grid grid-cols-2 gap-4">
                    <div className="relative flex min-h-0 min-w-0 flex-row items-center gap-3 overflow-hidden rounded-r10 border border-black80 bg-black90 p-3">
                        {armConnected ? (
                            <span className="absolute right-2 top-2 rounded-r5 border border-primary2 bg-primary2 px-2 py-0.5 text-h5 font-semibold text-white">
                                {t("payload.active")}
                            </span>
                        ) : null}
                        <div className="relative flex h-24 w-28 shrink-0 items-center justify-center rounded-r5 bg-black100 text-white">
                            <ControlArmIcon
                                className="h-14 w-14 shrink-0"
                                style={{ width: "3.5rem", height: "3.5rem" }}
                            />
                        </div>
                        <div className="min-w-0 py-0.5">
                            <p className="text-h5 font-semibold leading-snug text-white">
                                {t("payload.arm.title")}
                            </p>
                            <p className="mt-1 line-clamp-3 text-h5 leading-snug text-black40">
                                {t("payload.arm.description")}
                            </p>
                            <div className="mt-2 inline-flex flex-col items-stretch">
                                <FunctionalButton
                                    title={armStatusLabel}
                                    className="rounded-r5 border border-black60 bg-black80 px-3 py-1.5 !normal-case text-h5 font-semibold text-black40 disabled:cursor-not-allowed disabled:opacity-50"
                                    disabled
                                />
                                <p className="mt-1 w-full text-center text-[10px] leading-tight text-black60">
                                    {armHint}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex flex-col gap-3">
                <div>
                    <p className="text-h4 font-semibold text-white">{t("payload.gimbalsTitle")}</p>
                    <p className="mt-1 text-h5 text-black40">
                        <Trans
                            i18nKey="payload.description"
                            ns="settings"
                            components={{
                                1: <span className="text-white" />,
                                2: <span className="text-white" />,
                            }}
                        />
                    </p>
                    {!baseUrl ? (
                        <p className="mt-2 text-h5 text-black60">
                            {t("payload.connectFirst")}
                        </p>
                    ) : null}
                </div>
                <div
                    className="grid grid-cols-2 gap-4"
                    role="listbox"
                    aria-label={t("payload.listboxAriaLabel")}
                >
                    {PAYLOAD_OPTIONS.map((opt) => {
                        const isSel = selected === opt.id;
                        const isActive = currentProfile === opt.id;
                        const settingsEnabled = opt.id === "skydroid";
                        return (
                            <div
                                key={opt.id}
                                role="option"
                                aria-selected={isSel}
                                tabIndex={0}
                                onClick={() => setSelected(opt.id)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        setSelected(opt.id);
                                    }
                                }}
                                className={`relative flex min-h-0 min-w-0 cursor-pointer flex-row items-center gap-3 overflow-hidden rounded-r10 border bg-black90 p-3 text-left transition-colors ${
                                    isSel
                                        ? "border-primary2 ring-2 ring-primary2/40"
                                        : "border-black80 hover:border-black60"
                                }`}
                            >
                                {isActive ? (
                                    <span className="absolute right-2 top-2 rounded-r5 border border-primary2 bg-primary2 px-2 py-0.5 text-h5 font-semibold text-white">
                                        {t("payload.active")}
                                    </span>
                                ) : null}
                                <div className="relative h-24 w-28 shrink-0 rounded-r5 bg-black100">
                                    <img
                                        src={opt.image}
                                        alt=""
                                        className="absolute inset-0 m-auto max-h-[5.5rem] max-w-full object-contain p-1"
                                    />
                                </div>
                                <div className="min-w-0 flex-1 py-0.5 pr-2">
                                    <p className="text-h5 font-semibold leading-snug text-white">
                                        {t(`payload.options.${opt.id}.title`)}
                                    </p>
                                    <p className="mt-1 line-clamp-3 text-h5 leading-snug text-black40">
                                        {t(`payload.options.${opt.id}.subtitle`)}
                                    </p>
                                    <button
                                        type="button"
                                        className={`mt-2 rounded-r5 border px-2 py-1 text-h5 font-semibold !normal-case ${
                                            settingsEnabled
                                                ? "border-black60 text-white hover:border-black40"
                                                : "cursor-not-allowed border-black80 text-black60 opacity-50"
                                        }`}
                                        disabled={!settingsEnabled}
                                        aria-disabled={!settingsEnabled}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (!settingsEnabled) return;
                                            setThermalDialogOpen(true);
                                        }}
                                    >
                                        {t("payload.settingsButton")}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <FunctionalButton
                        title={busy ? t("common:saving") : t("common:save")}
                        className="rounded-r5 border border-primary2 bg-primary2 px-4 py-2 !normal-case text-h5 font-semibold text-white hover:bg-primary1 disabled:opacity-50"
                        disabled={!baseUrl || !selected || busy || loadingSelected}
                        onClick={() => void applyProfile()}
                    />
                    <FunctionalButton
                        title={busy ? t("common:working") : t("common:disconnect")}
                        className="rounded-r5 border border-black60 bg-black90 px-4 py-2 !normal-case text-h5 font-semibold text-white hover:border-black40 disabled:opacity-50"
                        disabled={!baseUrl || !selected || busy || loadingSelected}
                        onClick={() => void disconnectProfile()}
                    />
                </div>
            </div>

            <Insta360SettingsPanel />

            <div className="flex flex-col gap-3">
                <p className="text-h4 font-semibold text-white">{t("payload.securityTitle")}</p>
                <p className="text-h5 text-black40">{t("payload.securityCaption")}</p>
                <div className="grid grid-cols-2 gap-4">
                    <div className="relative flex min-h-0 min-w-0 flex-row items-center gap-3 overflow-hidden rounded-r10 border border-black80 bg-black90 p-3">
                        <div className="relative h-24 w-28 shrink-0 rounded-r5 bg-black100">
                            <img
                                src={patrolImage}
                                alt=""
                                className="absolute inset-0 m-auto max-h-[5.5rem] max-w-full object-contain p-1"
                            />
                        </div>
                        <div className="min-w-0 flex-1 py-0.5 pr-2">
                            <p className="text-h5 font-semibold leading-snug text-white">
                                {t("payload.patrol.title")}
                            </p>
                            <p className="mt-1 line-clamp-3 text-h5 leading-snug text-black40">
                                {t("payload.patrol.description")}
                            </p>
                            <button
                                type="button"
                                className="mt-2 rounded-r5 border border-black60 px-2 py-1 text-h5 font-semibold !normal-case text-white hover:border-black40"
                                onClick={() => setPatrolDialogOpen(true)}
                            >
                                {t("payload.settingsButton")}
                            </button>
                            <div className="mt-2 inline-flex flex-col items-stretch">
                                <FunctionalButton
                                    title={t(
                                        isSecurityModuleConnected
                                            ? "payload.patrol.connected"
                                            : "payload.patrol.disconnected"
                                    )}
                                    className="rounded-r5 border border-black60 bg-black80 px-3 py-1.5 !normal-case text-h5 font-semibold text-black40 disabled:cursor-not-allowed disabled:opacity-50"
                                    disabled
                                />
                                <p className="mt-1 w-full text-center text-[10px] leading-tight text-black60">
                                    {t(
                                        isSecurityModuleConnected
                                            ? "payload.patrol.disconnectHint"
                                            : "payload.patrol.connectHint"
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <SkydroidThermalSettingsDialog
                visible={thermalDialogOpen}
                onClose={() => setThermalDialogOpen(false)}
            />
            <PatrolModuleSettingsDialog
                visible={patrolDialogOpen}
                onClose={() => setPatrolDialogOpen(false)}
            />
        </div>
    );
};

export default PayloadSettingsPanel;
